import { dangerouslyGetFeatureGateSync } from '@trello/feature-gate-client';

import { orderedRouteList, routeDefinitions } from './routeDefinitions';
import { type RouteIdType } from './RouteId';

/**
 * Returns the route id of a provided URL pathname.
 *
 * @example
 * // returns 'powerUpPublicDirectory'
 * getRouteIdFromPathname('/power-ups');
 *
 * @param path - The path to get the route id from
 * @returns The route id
 * @throws {Error} If path name did not match any route ID.
 */
export const getRouteIdFromPathname = (path: string): RouteIdType => {
  let normalizedPath: string;

  const isGateEnabled =
    dangerouslyGetFeatureGateSync('tplat_migrate_route_member_activity') ||
    dangerouslyGetFeatureGateSync('tplat_migrate_route_member_profile') ||
    dangerouslyGetFeatureGateSync('tplat_migrate_route_member_cards');

  /**
   * - When gate enabled, the path is normalized by removing the leading slash and any query params.
   * - When gate disabled, the path is assumed to always start with a leading slash.
   */
  if (isGateEnabled) {
    // Normalize the path by removing the leading slash if present
    normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  } else {
    // Legacy behavior: assume path always starts with '/'
    normalizedPath = path.substring(1, path.length);
  }

  // remove any query params
  normalizedPath = normalizedPath.replace(/\?(.*)$/, '');

  for (const route of orderedRouteList) {
    if (normalizedPath.match(route.regExp)) {
      // Check if this is the enterprise admin route and check the feature gate
      if (
        route.id === 'enterpriseAdmin' &&
        !dangerouslyGetFeatureGateSync(
          'tplat_remove_redundant_enterprise_route_def',
        )
      ) {
        // If this matches the the enterprise admin tab route, return that route id instead if the gate is off (maintains existing behavior)
        if (routeDefinitions.enterpriseAdminTab.regExp.exec(normalizedPath)) {
          return routeDefinitions.enterpriseAdminTab.id;
        }
      }
      return route.id;
    }
  }

  throw new Error('Path name did not match any Route ID');
};
